import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import {
	Alert,
	Button,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Space,
	Tooltip,
	Typography,
} from 'antd';
import type { FC, ReactNode } from 'react';
import { AUTH_SERVICES_BASE_URL } from '@constants/url';
import Section from '@ui/Section';
import {
	ALLOWED_BY_PRINCIPAL_TYPE,
	type AssignmentFormState,
	getObjectDefaults,
	type PrincipalFormValues,
	type SelectOption,
} from './useAssignmentForm';
import styles from './styles.module.scss';

const { Title } = Typography;

type FieldSpec = {
	/** Form.Item name path — relative to the list item inside a Form.List. */
	name: string | (string | number)[];
	/** Stable suffix for React keys and test ids. */
	id: string;
	label: string;
	/** When present the field renders as a Select over these options. */
	options?: SelectOption[];
	disabled?: boolean;
	placeholder?: string;
	/** Shows a spinner in the Select while its options are being fetched. */
	loading?: boolean;
	/**
	 * Nothing to choose, so render the value as a read-only Input rather than a
	 * one-option dropdown. Not `disabled` — the field isn't inapplicable.
	 */
	readOnly?: boolean;
	/** Runs alongside the Form's own change handling, not instead of it. */
	onChange?: () => void;
};

/** One empty object row, so the list always starts with something to fill in. */
const INITIAL_VALUES = {
	objects: [{}],
};

/** The types this form can be asked to choose between. */
const PRINCIPAL_TYPE_OPTIONS: SelectOption[] = [
	{ key: 'app', value: 'App' },
	{ key: 'companyGroup', value: 'Company groups' },
];

/**
 * Not in PRINCIPAL_TYPE_OPTIONS: it isn't one of the types to pick from, it's
 * the one the user assignment form is fixed to.
 */
export const USER_PRINCIPAL_TYPE: SelectOption = { key: 'user', value: 'User' };

const ROLE_OPTIONS: SelectOption[] = [
	{ key: 'admin', value: 'Admin' },
	{ key: 'creator', value: 'Creator' },
	{ key: 'editor', value: 'Editor' },
	{ key: 'group', value: 'Group' },
	{ key: 'owner', value: 'Owner' },
	{ key: 'viewer', value: 'Viewer' },
];

/**
 * Every object type the form knows. ALLOWED_BY_PRINCIPAL_TYPE decides which of
 * them a given principal type may pick from, so adding one here doesn't put it
 * in front of a principal that has a list of its own.
 */
const OBJECT_TYPE_OPTIONS: SelectOption[] = [
	{ key: 'company', value: 'Company' },
	{ key: 'companyGroup', value: 'Companies group' },
	{ key: 'pcc', value: 'PCC' },
	{ key: 'globalGroup', value: 'Global group' },
];

/** Undefined `allowed` means no restriction — no principal type picked yet. */
const filterOptions = (options: SelectOption[], allowed: string[] | undefined): SelectOption[] =>
	allowed ? options.filter(({ key }) => allowed.includes(key)) : options;

/** The form stores option keys; this is what the user should see instead. */
const labelFor = (options: SelectOption[] | undefined, key: unknown): unknown =>
	options?.find((option) => option.key === key)?.value ?? key;

/**
 * The inverse: resolves a displayed label back to the key that gets submitted,
 * so the payload keeps `company` / `companyGroup` rather than what's on screen.
 */
const keyFor = (options: SelectOption[] | undefined, value: unknown): unknown =>
	options?.find((option) => option.value === value)?.key ?? value;

type FieldState = Pick<
	AssignmentFormState,
	| 'principalType'
	| 'principal'
	| 'objects'
	| 'companyGroupOptions'
	| 'isLoadingCompanyGroups'
	| 'clearObjectId'
>;

type FirstRowOptions = {
	fixedPrincipalType?: SelectOption;
	/** What the principal is called on this page. */
	principalLabel: string;
};

/**
 * Principal changes control with the principal type: a free-text client ID for
 * an app, a dropdown of company groups for a company group. Anything else has
 * no source for the value yet, so it stays read-only.
 *
 * A fixed type is the simple case — nothing to choose, so the type is shown
 * read-only and the principal is plain text.
 */
const getFirstRow = (
	{ principalType, companyGroupOptions, isLoadingCompanyGroups }: FieldState,
	{ fixedPrincipalType: fixed, principalLabel }: FirstRowOptions
): FieldSpec[] => {
	const isApp = principalType === 'app';
	const isGroupPrincipal = !fixed && principalType === 'companyGroup';

	return [
		{
			name: 'principalType',
			id: 'principalType',
			label: 'Principal type',
			options: fixed ? [fixed] : PRINCIPAL_TYPE_OPTIONS,
			// One legal value, so it's filled in rather than asked for.
			readOnly: !!fixed,
		},
		{
			name: 'principal',
			id: 'principal',
			label: principalLabel,
			disabled: !fixed && !isApp && !isGroupPrincipal,
			options: isGroupPrincipal ? companyGroupOptions : undefined,
			loading: isGroupPrincipal && isLoadingCompanyGroups,
			placeholder: isApp ? 'Enter Client ID' : undefined,
		},
	];
};

/**
 * One repeatable object row. Names are relative to the Form.List item, so they
 * take the row's index. Object ID follows this row's own object type: a
 * dropdown for a company group, plain text for a company.
 */
const getObjectFields = (
	index: number,
	state: FieldState,
	fixedPrincipalType?: SelectOption
): FieldSpec[] => {
	const { principalType, principal, objects } = state;
	const isGroupObject = objects[index]?.objectType === 'companyGroup';
	const allowed = principalType ? ALLOWED_BY_PRINCIPAL_TYPE[principalType] : undefined;
	const roleOptions = filterOptions(ROLE_OPTIONS, allowed?.roles);
	const objectTypeOptions = filterOptions(OBJECT_TYPE_OPTIONS, allowed?.objectTypes);

	// These wait for a principal only where the principal type is still in play:
	// on the app path, picking the type is the first thing you do, so filling in
	// objects before naming the app puts the cart before the horse. Where the
	// type is already settled — fixed, or a company group, which only ever
	// assigns one thing — there's nothing to wait for.
	const disabled = !fixedPrincipalType && principalType !== 'companyGroup' && !principal;

	return [
		{
			name: [index, 'role'],
			id: `objects-${index}-role`,
			label: 'Role',
			options: roleOptions,
			// A single allowed role leaves nothing to pick.
			readOnly: roleOptions.length === 1,
			disabled,
		},
		{
			name: [index, 'objectType'],
			id: `objects-${index}-objectType`,
			label: 'Object type',
			options: objectTypeOptions,
			// A single allowed object type leaves nothing to pick.
			readOnly: objectTypeOptions.length === 1,
			// This row's object id was picked for the old type.
			onChange: () => state.clearObjectId(index),
			disabled,
		},
		{
			name: [index, 'objectId'],
			id: `objects-${index}-objectId`,
			label: 'Object ID',
			disabled,
			options: isGroupObject ? state.companyGroupOptions : undefined,
			loading: isGroupObject && state.isLoadingCompanyGroups,
		},
	];
};

const renderField = ({
	name,
	id,
	label,
	options,
	disabled,
	placeholder,
	loading,
	readOnly,
	onChange,
}: FieldSpec) => {
	// Checked before `options`, since a read-only field can still have them —
	// getValueProps shows the option's label while the key stays submitted.
	if (readOnly) {
		return (
			<Form.Item
				name={name}
				label={`${label} (read-only)`}
				getValueProps={(value) => ({ value: labelFor(options, value) })}
				// Belt and braces: the field is read-only so the label can't be
				// typed back in, but if that ever changes the key still wins.
				normalize={(value) => keyFor(options, value)}
				rules={[{ required: true, message: `${label} is required` }]}
			>
				<Input
					readOnly
					disabled={disabled}
					data-testid={`assignment-${id}`}
				/>
			</Form.Item>
		);
	}

	return (
		<Form.Item
			name={name}
			label={label}
			// whitespace only means anything for a free-text value.
			rules={[{ required: true, whitespace: !options, message: `${label} is required` }]}
		>
			{options ? (
				<Select
					options={options.map(({ key, value }) => ({ value: key, label: value }))}
					disabled={disabled}
					loading={loading}
					onChange={onChange}
					data-testid={`assignment-${id}`}
				/>
			) : (
				<Input
					disabled={disabled}
					placeholder={placeholder}
					data-testid={`assignment-${id}`}
				/>
			)}
		</Form.Item>
	);
};

/**
 * Three equal columns on md and up, stacked below that. `trailing` is expected
 * to be absolutely positioned, so it takes none of the row's width.
 */
const renderRow = (fields: FieldSpec[], trailing?: ReactNode, key?: string) => (
	<Row
		key={key}
		gutter={16}
		className={styles.fieldRow}
	>
		{fields.map((field) => (
			<Col
				key={field.id}
				xs={24}
				// However many fields a row has, they share its full width.
				md={24 / fields.length}
			>
				{renderField(field)}
			</Col>
		))}
		{trailing}
	</Row>
);

type AssignmentFormProps = {
	state: AssignmentFormState;
	/**
	 * Fixes the principal type rather than asking for it: the field renders
	 * read-only at this value and Principal becomes plain text.
	 */
	fixedPrincipalType?: SelectOption;
	/**
	 * What the principal is called here. The field submits as `principal`
	 * whatever it's labelled, and its validation message follows the label.
	 */
	principalLabel?: string;
	/**
	 * Says why Submit is off, for a form with nowhere to post to. Only rendered
	 * when the state has no submit.
	 */
	unavailableNotice?: string;
};

const AssignmentForm: FC<AssignmentFormProps> = ({
	state,
	fixedPrincipalType,
	principalLabel = 'Principal',
	unavailableNotice,
}) => {
	const {
		form,
		principalType,
		canAddObject,
		handleValuesChange,
		confirmRemoveObject,
		submitAssignment,
		isSubmitting,
		result,
		canSubmit,
	} = state;

	// A fixed type is never asked for, so it starts already filled in.
	const initialValues = fixedPrincipalType
		? { ...INITIAL_VALUES, principalType: fixedPrincipalType.key }
		: INITIAL_VALUES;

	return (
		<Section white spacious>
			{!canSubmit && unavailableNotice && (
				<Alert
					type="info"
					showIcon
					message={unavailableNotice}
					data-testid="assignment-unavailable"
				/>
			)}
			<Form<PrincipalFormValues>
				form={form}
				className={styles.form}
				layout="vertical"
				initialValues={initialValues}
				disabled={!AUTH_SERVICES_BASE_URL}
				onValuesChange={handleValuesChange}
				onFinish={submitAssignment}
			>
				{renderRow(getFirstRow(state, { fixedPrincipalType, principalLabel }))}
				{/* The operation this form always performs, named on the line
				    that separates the principal from what it's granted. */}
				<Divider className={styles.rowDivider}>assign</Divider>
				<Form.List name="objects">
					{(rows, { add, remove }) => (
						<>
							{rows.map(({ key, name }) =>
								renderRow(
									getObjectFields(name, state, fixedPrincipalType),
									rows.length > 1 && (
										<Tooltip title={`Remove object ${name + 1}`}>
											<Button
												type="text"
												size="small"
												className={styles.removeButton}
												icon={<CloseOutlined />}
												onClick={() =>
													confirmRemoveObject(name, () => remove(name))
												}
												aria-label={`Remove object ${name + 1}`}
												data-testid={`assignment-remove-object-${name}`}
											/>
										</Tooltip>
									),
									String(key)
								)
							)}
							<Form.Item>
								<Button
									type="link"
									size="small"
									icon={<PlusOutlined />}
									disabled={!canAddObject}
									onClick={() => add(getObjectDefaults(principalType))}
									data-testid="assignment-add-object"
								>
									Add object
								</Button>
							</Form.Item>
						</>
					)}
				</Form.List>
				<Divider className={styles.actionsDivider} />
				<Space>
					<Button
						type="primary"
						htmlType="submit"
						loading={isSubmitting}
						// undefined rather than false, so the Form's own disabled
						// state still reaches the button when there is a submit.
						disabled={canSubmit ? undefined : true}
						data-testid="assignment-submit"
					>
						Submit
					</Button>
					<Button
						htmlType="button"
						onClick={() => form.resetFields()}
						data-testid="assignment-reset"
					>
						Reset
					</Button>
				</Space>
			</Form>
			{result !== undefined && (
				<div className={styles.panel}>
					<Title level={5}>Response</Title>
					<pre
						className={styles.code}
						data-testid="assignment-result"
					>
						{JSON.stringify(result, null, 2)}
					</pre>
				</div>
			)}
		</Section>
	);
};

export default AssignmentForm;
